import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Ship,
  Plane,
  Truck,
  Train,
  Coins,
} from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import {
  freightLegCatalogService,
  type CatalogFreightLeg,
} from '../services/freight-leg-catalog-service';
import { LegCatalogFormDialog } from './leg-catalog-form-dialog';
import { LegCatalogPricingDialog } from './leg-catalog-pricing-dialog';
import {
  TransportMode,
  TRANSPORT_MODE_LABELS,
  FREIGHT_LEG_TYPE_LABELS,
} from '../types/freight-route.types';

const TRANSPORT_ICONS: Record<TransportMode, React.ReactNode> = {
  SEA: <Ship className="w-4 h-4" />,
  AIR: <Plane className="w-4 h-4" />,
  LAND: <Truck className="w-4 h-4" />,
  RAIL: <Train className="w-4 h-4" />,
  MULTIMODAL: <Truck className="w-4 h-4" />,
};

type DialogState =
  | { open: false }
  | { open: true; mode: { kind: 'create' } }
  | { open: true; mode: { kind: 'edit'; leg: CatalogFreightLeg } };

export function FreightLegCatalogTable() {
  const { toast } = useToast();
  const [legs, setLegs] = useState<CatalogFreightLeg[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [modeFilter, setModeFilter] = useState<string>('');
  const [dialogState, setDialogState] = useState<DialogState>({ open: false });
  const [pricingLegId, setPricingLegId] = useState<string | null>(null);

  const fetchLegs = async () => {
    setIsLoading(true);
    try {
      const data = await freightLegCatalogService.list({
        q: searchQ || undefined,
        mode: (modeFilter as TransportMode) || undefined,
      });
      setLegs(data);
    } catch (error: any) {
      toast({
        title: 'Error al cargar catálogo',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLegs();
  }, [searchQ, modeFilter]);

  const handleDelete = async (leg: CatalogFreightLeg) => {
    if (
      !(await confirmar({
        title: `¿Eliminar tramo ${leg.origin} → ${leg.destination}?`,
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await freightLegCatalogService.remove(leg.id);
      toast({ title: 'Tramo eliminado', variant: 'success' });
      fetchLegs();
    } catch (error: any) {
      toast({
        title: 'No se pudo eliminar',
        description: error.message || 'Error al eliminar',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Buscar por origen o destino..."
            className="pl-9"
          />
        </div>
        <Select
          value={modeFilter || '__all__'}
          onValueChange={(v) => setModeFilter(v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Todos los modos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los modos</SelectItem>
            {Object.entries(TRANSPORT_MODE_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => setDialogState({ open: true, mode: { kind: 'create' } })}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo tramo
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : legs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchQ || modeFilter
            ? 'Sin tramos que coincidan con los filtros.'
            : 'Sin tramos en el catálogo. Haz clic en "Nuevo tramo" para empezar.'}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Modo</TableHead>
              <TableHead>Origen → Destino</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-center">Estado</TableHead>
              <TableHead className="text-right w-32">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {legs.map((leg) => (
              <TableRow key={leg.id} className="hover:bg-muted/50">
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {TRANSPORT_ICONS[leg.transportMode]}
                    <span className="text-sm">{TRANSPORT_MODE_LABELS[leg.transportMode]}</span>
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  {leg.origin} → {leg.destination}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{FREIGHT_LEG_TYPE_LABELS[leg.legType]}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={leg.isActive ? 'default' : 'secondary'}>
                    {leg.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setPricingLegId(leg.id)}
                      title="Configurar precios por proveedor"
                      aria-label={`Configurar precios de ${leg.origin} → ${leg.destination}`}
                    >
                      <Coins className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDialogState({ open: true, mode: { kind: 'edit', leg } })}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {/* Only standalone legs (routeId=null) can be deleted */}
                    {!leg.routeId && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(leg)}>
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create / Edit dialog */}
      {dialogState.open && (
        <LegCatalogFormDialog
          open={dialogState.open}
          onOpenChange={(open) => {
            if (!open) setDialogState({ open: false });
          }}
          mode={dialogState.mode}
          onSuccess={() => {
            fetchLegs();
            setDialogState({ open: false });
          }}
        />
      )}

      {/* Provider pricing dialog */}
      {pricingLegId && (
        <LegCatalogPricingDialog
          legId={pricingLegId}
          open={!!pricingLegId}
          onOpenChange={(open) => {
            if (!open) setPricingLegId(null);
          }}
          onSaved={fetchLegs}
        />
      )}
    </div>
  );
}
